<template>
  <div class="pay-container" data-cy="pay-on-card-reader-screen">
    <v-btn
      v-if="showResetButton"
      class="close-button"
      dense
      text
      icon
      @click="startNewSession"
    >
      <v-progress-circular
        v-if="resetting"
        color="black"
        size="20"
        width="2"
        indeterminate
      />
      <v-icon v-else color="black" dense>mdi-replay</v-icon>
    </v-btn>
    <div v-if="loading">
      <v-progress-circular indeterminate />
    </div>
    <div v-else class="pay-content-container">
      <div class="pay-card">
        <div class="icon-container">
          <div class="icon-content-container">
            <v-img src="/assets/icons/bill.svg" max-width="50px" />
          </div>
        </div>
        <div class="my-10 text-center">
          <InPersonPaymentStatus
            ref="status"
            :dialog-text="dialogText"
            @abort="stop"
          />
        </div>
      </div>
      <InPersonPaymentErrorBottomSheet
        ref="errorBottomSheet"
        :show-bank-error="false"
        @retry="start"
        @startOver="startNewSession"
      />
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import InPersonPaymentStatus from '@/components/Payments/InPersonPaymentStatus.vue';
import InPersonPaymentErrorBottomSheet from '@/components/Payments/InPersonPaymentErrorBottomSheet.vue';
import { getKioskOrderLink } from '@/utils/orders';

// Card payment on the kiosk's own card reader. greet-menu-api asks
// payments-api to prompt the reader; this screen only starts the payment,
// polls it until it is final, and can ask the reader to stop. It never talks
// to the reader itself and never decides whether the card was charged.

const READER_STATUSES = {
  pending: 'pending',
  processing: 'processing',
  stopping: 'stopping',
  succeeded: 'succeeded',
  failed: 'failed',
};

const SCREEN_STATES = {
  idle: 'idle',
  paying: 'paying',
  completing: 'completing',
  error: 'error',
};

// What the error sheet shows for each reason payments-api gives
const FAILURE_TO_ERROR_CODE = {
  1000: 'UC', // cancelled on the reader
  1003: 'NO_CARD', // the reader timed out
  1004: 'DECLINED',
  1006: 'DECLINED',
  1007: 'DECLINED',
  1016: 'TA', // stopped
  stopped: 'TA',
  1020: '116', // insufficient funds
  1100: 'BS', // the reader is busy with another payment
  reader_unreachable: 'NR',
  // paid, but not accepted for this order: the money is returned
  not_accepted: 'ORDER_CREATE_FAILED',
};

const POLL_INTERVAL_MS = 1000;
const ORDER_SYNC_INTERVAL_MS = 1000;
// payments-api gives up on a reader that never answers after 90-150 seconds
const READER_WATCHDOG_MS = 180 * 1000;
// after the card is charged we only wait on our own backend
const COMPLETING_ORDER_WATCHDOG_MS = 60 * 1000;
const STOP_RESET_BUTTON_MS = 15 * 1000;

export default {
  components: {
    InPersonPaymentStatus,
    InPersonPaymentErrorBottomSheet,
  },
  data() {
    return {
      loading: false,
      initialized: false,
      screenState: SCREEN_STATES.idle,
      paymentUUID: null,
      stopRequested: false,
      dialogText: null,
      pollTimeoutId: null,
      isPolling: false,
      orderSyncIntervalId: null,
      isSyncingOrders: false,
      watchdogId: null,
      stopResetTimeoutId: null,
      showResetButton: false,
      resetting: false,
    };
  },
  computed: {
    order() {
      return this.$store.getters.getOrderByUniqueOrderNumber(
        this.$route.params.order
      );
    },
  },
  watch: {
    order(newOrder) {
      if (newOrder && this.screenState === SCREEN_STATES.completing) {
        this.handleOrderUpdate(newOrder);
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopOrderSync();
    this.clearWatchdog();
    if (this.stopResetTimeoutId) {
      clearTimeout(this.stopResetTimeoutId);
    }
  },
  async mounted() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      this.loading = true;

      try {
        await this.$store.dispatch('fetchSessionData', {
          session: this.$route.params.session,
        });
        await this.$store.dispatch('fetchOrders');
        await this.$store.dispatch('subscribeToPusherChannels');
      } catch (error) {
        console.error('PAY_ON_CARD_READER: initialization failed', error);
        this.loading = false;
        await this.$nextTick();
        this.$refs?.errorBottomSheet?.open('GENERIC');
        return;
      }

      this.initialized = true;
      this.loading = false;
      await this.$nextTick();
      this.start();
    },
    // Also the error sheet's retry: while a payment for the order is still
    // open, greet-menu-api answers with that one instead of a new prompt.
    async start() {
      if (!this.initialized) {
        await this.initialize();
        return;
      }
      if (this.screenState === SCREEN_STATES.paying) {
        return;
      }

      this.screenState = SCREEN_STATES.paying;
      this.paymentUUID = null;
      this.stopRequested = false;
      this.showResetButton = false;
      this.startWatchdog(READER_WATCHDOG_MS);
      this.showStatus(this.$t('kiosk.payOnCardReader.preparing'), true);

      let payment;
      try {
        payment = await this.$store.dispatch('startCardReaderPayment', {
          order: this.$route.params.order,
        });
      } catch (error) {
        console.error('PAY_ON_CARD_READER: failed to start the payment', error);
        this.fail('GENERIC');
        return;
      }

      this.paymentUUID = payment.uuid;

      // the guest pressed stop before the reader was asked
      if (this.stopRequested) {
        this.sendStop();
      }

      this.handlePayment(payment);
    },
    async poll() {
      this.pollTimeoutId = null;
      if (this.isPolling || !this.paymentUUID) {
        return;
      }

      this.isPolling = true;
      try {
        const payment = await this.$store.dispatch('fetchCardReaderPayment', {
          paymentUUID: this.paymentUUID,
        });
        this.handlePayment(payment);
      } catch (error) {
        // a lost answer says nothing about the card: keep asking
        console.warn('PAY_ON_CARD_READER: status read failed', error);
        this.schedulePoll();
      } finally {
        this.isPolling = false;
      }
    },
    handlePayment(payment) {
      if (this.screenState !== SCREEN_STATES.paying) {
        return;
      }

      switch (payment?.status) {
        case READER_STATUSES.succeeded:
          this.stopPolling();
          this.completeOrder();
          return;
        case READER_STATUSES.failed:
          this.stopPolling();
          this.fail(this.errorCodeFor(payment.failure));
          return;
        default:
          this.showPending(payment?.status);
      }

      this.schedulePoll();
    },
    // Not final yet. Once the guest asked to stop, stopping is all the screen
    // says until the payment ends.
    showPending(status) {
      if (this.stopRequested || status === READER_STATUSES.stopping) {
        this.showStatus(this.$t('kiosk.payOnCardReader.stopping'), false);
        return;
      }

      switch (status) {
        case READER_STATUSES.processing:
          this.showStatus(
            this.$t('kiosk.payOnCardReader.followInstructions'),
            true
          );
          break;
        default:
          this.showStatus(this.$t('kiosk.payOnCardReader.preparing'), true);
      }
    },
    // Asking the reader to stop is a request: keep polling until the payment
    // ends, as the card may already have been read.
    async stop() {
      this.stopRequested = true;
      this.showStatus(this.$t('kiosk.payOnCardReader.stopping'), false);
      this.showResetButtonLater();

      if (this.paymentUUID) {
        await this.sendStop();
      }
    },
    async sendStop() {
      try {
        await this.$store.dispatch('stopCardReaderPayment', {
          paymentUUID: this.paymentUUID,
        });
      } catch (error) {
        console.error('PAY_ON_CARD_READER: failed to stop the payment', error);
      }
    },
    completeOrder() {
      this.screenState = SCREEN_STATES.completing;
      this.startWatchdog(COMPLETING_ORDER_WATCHDOG_MS);
      this.showStatus(this.$t('kiosk.payInPerson.terminal.completingOrder'), false);
      this.startOrderSync();

      if (this.order) {
        this.handleOrderUpdate(this.order);
      }
    },
    handleOrderUpdate(order) {
      const sendingToPOS = order.sending_to_pos;
      const orderSentToPOS = order.sent_to_pos;
      const integratedToPOS = this.$store.getters.posIntegrationEnabled;

      if (order.status === 'failed') {
        // greet-menu-api refunds the card on its own
        this.stopOrderSync();
        this.fail('ORDER_CREATE_FAILED');
      } else if (sendingToPOS && orderSentToPOS) {
        this.finish(order);
      } else if (sendingToPOS) {
        this.dialogText = [this.$t('kiosk.payInPerson.terminal.sendingToPOS')];
      } else if (!integratedToPOS && order.payments?.length > 0) {
        this.finish(order);
      }
    },
    finish(order) {
      this.stopOrderSync();
      this.clearWatchdog();
      this.printReceipt(order);

      this.$router.push({
        name: 'PayInPersonSuccess',
        query: {
          orderNumber: this.$route.params.order,
        },
        params: {
          session: this.$store.state.session,
          paymentUUID: this.paymentUUID,
        },
      });
    },
    fail(errorCode) {
      this.screenState = SCREEN_STATES.error;
      this.stopPolling();
      this.clearWatchdog();
      this.$refs?.status?.hide();
      this.$refs?.errorBottomSheet?.open(errorCode);
    },
    errorCodeFor(failure) {
      return FAILURE_TO_ERROR_CODE[failure?.code] || 'GENERIC';
    },
    printReceipt(order) {
      const QRLink = getKioskOrderLink(
        this.$store.state.session,
        order.unique_order_number,
        { force: true }
      );

      this.$store.dispatch('printReceipt', {
        text: '',
        orderId: order.order_id,
        additionalFields: Array.isArray(order.additional_fields)
          ? order.additional_fields
          : [],
        QRLink,
        items: order.items || [],
        discount: order.discount,
        discountCode: order.coupon,
      });
    },
    async startNewSession() {
      this.resetting = true;

      // a payment still on the reader must not be left prompting
      if (this.screenState === SCREEN_STATES.paying && this.paymentUUID) {
        this.$store
          .dispatch('stopCardReaderPayment', { paymentUUID: this.paymentUUID })
          .catch(error =>
            console.error('PAY_ON_CARD_READER: failed to stop the payment', error)
          );
      }

      try {
        await this.$store.dispatch('generateNewKioskSession');
      } catch (error) {
        console.error('PAY_ON_CARD_READER: failed to start a new session', error);
        this.resetting = false;
      }
    },
    showStatus(text, showCancel) {
      this.dialogText = [text];
      this.$refs?.status?.show({ showCancel });
    },
    schedulePoll() {
      if (this.pollTimeoutId) {
        return;
      }
      this.pollTimeoutId = setTimeout(this.poll, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimeoutId) {
        clearTimeout(this.pollTimeoutId);
        this.pollTimeoutId = null;
      }
    },
    startOrderSync() {
      this.stopOrderSync();
      this.orderSyncIntervalId = setInterval(
        this.syncOrders,
        ORDER_SYNC_INTERVAL_MS
      );
    },
    stopOrderSync() {
      if (this.orderSyncIntervalId) {
        clearInterval(this.orderSyncIntervalId);
        this.orderSyncIntervalId = null;
      }
    },
    async syncOrders() {
      if (this.isSyncingOrders) {
        return;
      }

      this.isSyncingOrders = true;
      try {
        await this.$store.dispatch('fetchOrders');
      } catch (error) {
        console.warn('PAY_ON_CARD_READER: order sync failed', error);
      } finally {
        this.isSyncingOrders = false;
      }
    },
    // Nothing resolved in time: let staff start over from the screen.
    startWatchdog(timeoutMs) {
      this.clearWatchdog();
      this.watchdogId = setTimeout(() => {
        this.watchdogId = null;
        this.showResetButton = true;

        if (this.screenState === SCREEN_STATES.completing) {
          Sentry.captureMessage(
            'PAY_ON_CARD_READER: card charged, order not confirmed in time',
            {
              level: 'error',
              extra: {
                paymentUUID: this.paymentUUID,
                orderNumber: this.$route.params.order,
              },
            }
          );
        }
      }, timeoutMs);
    },
    clearWatchdog() {
      if (this.watchdogId) {
        clearTimeout(this.watchdogId);
        this.watchdogId = null;
      }
    },
    showResetButtonLater() {
      if (this.stopResetTimeoutId) {
        clearTimeout(this.stopResetTimeoutId);
      }
      this.stopResetTimeoutId = setTimeout(() => {
        this.stopResetTimeoutId = null;
        if (this.screenState === SCREEN_STATES.paying) {
          this.showResetButton = true;
        }
      }, STOP_RESET_BUTTON_MS);
    },
  },
};
</script>

<style lang="scss" scoped>
.pay-container {
  min-height: 100vh;
  justify-content: center;
  align-items: center;
  display: flex;
  background-size: 400% 400%;
}

.pay-content-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: white;
  border-radius: 10px;
  width: 100%;
  margin-left: 40px;
  margin-right: 40px;
  margin-bottom: 40px;
  padding: 40px;
}

.icon-container {
  margin-top: -100px;
}

.icon-content-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background: black;
  width: 120px;
  height: 120px;
  border-radius: 50%;
}

.pay-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
}
</style>
